import type { Components, Theme } from '@mui/material/styles';
import { RADIUS, soft } from './soft';

const SEVERITIES = ['success', 'info', 'warning', 'error'] as const;

export const surfaces: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      // Inter's tabular figures also widen hyphens ("Sep-26"), so they apply to tables, not running text.
      body: { WebkitFontSmoothing: 'antialiased' },
      'table, .MuiTableCell-root': { fontVariantNumeric: 'tabular-nums' },
      '::selection': { backgroundColor: soft(theme.palette.primary.main, 18) },
      // Ripples are off, so keyboard focus needs its own clear ring.
      ':focus-visible, .Mui-focusVisible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
      },
      '.MuiOutlinedInput-input:focus-visible': { outline: 'none' },
    }),
  },
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      rounded: { borderRadius: RADIUS.container },
      outlined: ({ theme }) => ({ borderColor: theme.palette.divider }),
    },
  },
  MuiCard: {
    defaultProps: { variant: 'outlined' },
  },
  MuiCardContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(2.5),
        '&:last-child': { paddingBottom: theme.spacing(2.5) },
        [theme.breakpoints.up('md')]: { padding: theme.spacing(3), '&:last-child': { paddingBottom: theme.spacing(3) } },
      }),
    },
  },
  MuiAppBar: {
    defaultProps: { elevation: 0, color: 'inherit' },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => ({ borderColor: theme.palette.divider }),
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme }) => ({ border: `1px solid ${theme.palette.divider}`, boxShadow: theme.shadows[8] }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.body2,
        minHeight: 36,
        borderRadius: 6,
        marginInline: 4,
        '&.Mui-selected': { backgroundColor: soft(theme.palette.primary.main, 8) },
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({ borderRadius: RADIUS.container, boxShadow: theme.shadows[16] }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({ ...theme.typography.h3, padding: theme.spacing(2.5, 3, 1) }),
    },
  },
  MuiDialogContent: {
    styleOverrides: { root: ({ theme }) => ({ padding: theme.spacing(1, 3, 2) }) },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(1.5, 3),
        gap: theme.spacing(1),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.surface.subtle,
        '& > :not(style) ~ :not(style)': { marginLeft: 0 },
      }),
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.body2,
        borderRadius: theme.shape.borderRadius,
        alignItems: 'flex-start',
        variants: SEVERITIES.map((severity) => ({
          props: { variant: 'outlined', severity },
          style: {
            color: theme.palette.text.primary,
            backgroundColor: soft(theme.palette[severity].main, 6),
            borderColor: soft(theme.palette[severity].main, 28),
          },
        })),
      }),
      icon: { paddingTop: 8, fontSize: 20 },
    },
  },
  MuiSnackbarContent: {
    styleOverrides: {
      root: ({ theme }) => ({ ...theme.typography.body2, backgroundColor: theme.palette.primary.dark, borderRadius: theme.shape.borderRadius }),
    },
  },
  MuiDivider: {
    styleOverrides: { root: ({ theme }) => ({ borderColor: theme.palette.divider }) },
  },
};
